import React, { useState, useEffect, useContext, useMemo } from 'react';
import { KeycloakContext } from '../App';
import { MegaphoneIcon, PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { API_URL } from '../runtimeConfig';
import { useToast } from '../providers/ToastProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import { PageHeader } from '../components/ui/page-header';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { formatDateTime } from '../lib/format';

interface PatchNote {
  id: number;
  version: string;
  title: string;
  body: string;
  published: boolean;
  published_at?: string | null;
  created_at: string;
  updated_at: string;
}

interface Form {
  version: string;
  title: string;
  body: string;
  published: boolean;
}

const emptyForm: Form = { version: '', title: '', body: '', published: false };

export default function AdminChangelog() {
  const ctx = useContext(KeycloakContext);
  const keycloak = ctx?.keycloak;
  const { toast } = useToast();
  const { confirm } = useConfirm();

  const [notes, setNotes] = useState<PatchNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Editor (used for both create and edit)
  const [editing, setEditing] = useState<PatchNote | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<Form>(emptyForm);
  const [saving, setSaving] = useState(false);

  const open = creating || editing !== null;

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak?.token]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/admin/patch-notes`, {
        headers: { Authorization: `Bearer ${keycloak?.token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch patch notes');
      const data = await res.json();
      setNotes(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setCreating(true);
    setForm(emptyForm);
  };

  const openEdit = (n: PatchNote) => {
    setCreating(false);
    setEditing(n);
    setForm({ version: n.version, title: n.title, body: n.body, published: n.published });
  };

  const closeEditor = () => {
    setCreating(false);
    setEditing(null);
    setForm(emptyForm);
  };

  const handleSave = async () => {
    if (!form.version.trim() || !form.title.trim()) {
      toast('Version and title are required', 'error');
      return;
    }
    setSaving(true);
    try {
      const method = editing ? 'PUT' : 'POST';
      const url = editing
        ? `${API_URL}/admin/patch-notes/${editing.id}`
        : `${API_URL}/admin/patch-notes`;
      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${keycloak?.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to save');
      }
      toast(editing ? 'Patch note updated' : 'Patch note created', 'success');
      closeEditor();
      await fetchAll();
    } catch (err: any) {
      toast(err.message || 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (n: PatchNote) => {
    const ok = await confirm({
      title: 'Delete patch note',
      message: `Delete "${n.version} — ${n.title}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      const res = await fetch(`${API_URL}/admin/patch-notes/${n.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${keycloak?.token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to delete');
      }
      setNotes((prev) => prev.filter((x) => x.id !== n.id));
      toast('Patch note deleted', 'success');
    } catch (err: any) {
      toast(err.message || 'Failed to delete', 'error');
    }
  };

  const sorted = useMemo(() => [...notes].sort((a, b) => {
    // Most recently created first.
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  }), [notes]);

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader icon={<MegaphoneIcon />} title="Changelog" description="Author the patch notes shown to users in the dashboard." />
        <div className="rounded-lg border border-border bg-card p-6 space-y-3">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
        </div>
      </div>
    );
  }
  if (error) return <div className="rounded-md bg-destructive/10 p-4"><p className="text-sm text-destructive">Error: {error}</p></div>;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<MegaphoneIcon />}
        title="Changelog"
        description="Author the patch notes shown to users in the dashboard."
        actions={
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center gap-1.5 rounded-md border border-transparent bg-primary px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-primary/90"
          >
            <PlusIcon className="h-4 w-4" /> New entry
          </button>
        }
      />

      <div className="bg-card shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/30">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Version</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Updated</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {sorted.length === 0 ? (
                <tr><td colSpan={5} className="px-6 py-8 text-center text-sm text-muted-foreground">No patch notes yet. Click "New entry" to create the first one.</td></tr>
              ) : sorted.map((n) => (
                <tr key={n.id} className="hover:bg-muted/40">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-foreground">{n.version}</td>
                  <td className="px-6 py-4 text-sm text-foreground">{n.title}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {n.published ? (
                      <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset bg-success/10 text-success ring-success/20">Published</span>
                    ) : (
                      <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset bg-warning/10 text-warning ring-warning/20">Draft</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{formatDateTime(n.updated_at)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="inline-flex items-center gap-1">
                      <button type="button" onClick={() => openEdit(n)} title="Edit"
                        className="p-1.5 rounded-md text-primary hover:bg-primary/10 cursor-pointer transition-colors">
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button type="button" onClick={() => handleDelete(n)} title="Delete"
                        className="p-1.5 rounded-md text-destructive hover:bg-destructive/10 cursor-pointer transition-colors">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-background/70 backdrop-blur-sm" onClick={closeEditor} />
            <div className="inline-block align-bottom bg-card rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-card px-4 pt-5 pb-4 sm:p-6">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-xl font-semibold text-foreground">{editing ? `Edit patch note #${editing.id}` : 'New patch note'}</h3>
                  <button type="button" onClick={closeEditor} className="text-muted-foreground hover:text-foreground" aria-label="Close">
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Version</label>
                    <input type="text" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })}
                      placeholder="0.4.1" maxLength={64}
                      className="block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm font-mono focus-visible:ring-ring focus:border-primary" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Title</label>
                    <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                      placeholder="Stack public API + admin pages"
                      className="block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm focus-visible:ring-ring focus:border-primary" />
                  </div>
                  <div className="sm:col-span-3">
                    <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Body (Markdown)</label>
                    <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
                      rows={10}
                      placeholder={'- Added the public stacks API\n- Fixed badge colors in dark mode\n- See `/containers` for examples'}
                      className="block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm font-mono focus-visible:ring-ring focus:border-primary" />
                    <p className="mt-1 text-xs text-muted-foreground">Supports <strong>**bold**</strong>, <em>*italic*</em>, <code>`code`</code>, <code>[links](https://…)</code> and <code>- bullet lists</code>.</p>
                  </div>
                  <label className="sm:col-span-3 flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })}
                      className="h-4 w-4 rounded border-border text-primary focus:ring-ring" />
                    <span className="text-sm text-foreground">Published <span className="text-muted-foreground">— users see this in the "What's new" modal as soon as it's saved.</span></span>
                  </label>
                </div>
              </div>
              <div className="bg-muted/30 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : editing ? 'Save changes' : 'Create entry'}
                </Button>
                <Button variant="outline" onClick={closeEditor} disabled={saving}>Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
